/* eslint-disable max-len */
/* eslint-disable no-bitwise */

import { PortVisibility, ConnectorConstraints, PortConstraints, ConnectorDrawingTool, DiagramTools, NodeConstraints, AnnotationConstraints } from "@syncfusion/ej2-vue-diagrams";
import shortId from "short-uuid";
import symbols from "@/assets/images/ActivitiesIcons/syncfusion/symbols";
import { setTimeout } from "timers";
import store from "@/store";
import { diagramConfig } from "./DiagramConfig";

const dynamicPluginList = store().getters["apps/getDiagramExternalPlugins"] || {};

function checkExtraConditions(v) {
    let hasPluginType;
    const { activities, events, gateways } = dynamicPluginList;
    const allDynamicPlugins = [...(activities || []), ...(events || []), ...(gateways || [])];
    allDynamicPlugins.forEach(plugin => {
        if (!plugin.input && plugin.id === v) {
            hasPluginType = true;
        }
    });
    return hasPluginType;
}

export class ConnectorDraw extends ConnectorDrawingTool {
    diagram = null;

    vueInstance = null;

    node = null;

    mouseUp(args) {
        if (!args.source || (args.actualObject === args.source && args.source === args.target)) {
            return; // this a a library bug, if the handle is on top of another connector and
            // you click it without dragging, the args: source, target and actualObject get the value
            // of the innocent connector that gets erased in the next section just for being in the
            // wrong place at the wrong time. World is not a fair simulation...
        }
        if (args.source.propName === "connectors") {
            const found = this.diagram.connectors.find(el => el.sourceID === args.source.sourceID && el.targetID === args.source.targetID);
            const targeNode = this.diagram.nodes.find(node => node.id === args.source.targetID);
            if (
                args.target === null // no end target
                || !targeNode // target is not a node
                || found // connector with same source and target already exists
                || args.target === this.node // target is the same node
                || args.target.properties.data.typeId === "GENERAL_START_PLUGIN" // cannot connect back to start plugin
                || args.target.properties.data.typeId === "EXTERNAL_SOURCE_PLUGIN" // or start from external
                || args.target.properties.data.typeId === "START_BLOCKCHAIN_EVENT_PLUGIN" // or start blockchain
                || checkExtraConditions(args.target.properties.data.typeId)
            ) {
                this.diagram.dataBind();
                setTimeout(() => {
                    // no other way... no promise, so we need to wait one event loop cycle
                    this.diagram.remove(args.source);
                    this.diagram.select([this.node]);
                    this.diagram.dataBind();
                }, 0);
            } else {
                // happy path:
                const connector = {
                    id: shortId.uuid(),
                    type: args.source.type,
                    sourceID: args.source.sourceID,
                    targetID: args.source.targetID,
                    targetPortID: args.source.targetPortID
                };
                this.diagram.clearSelection();
                setTimeout(() => {
                    // no other way...
                    this.diagram.remove(args.source); // for some reason the connector is broken and we need to remove it
                    this.diagram.add(connector); // and add it back again with the info we saved
                    const last = this.diagram.connectors.find(({ id }) => id === connector.id);
                    if (last) {
                        this.diagram.select([last]);
                        this.diagram.sendBackward();
                    }
                    this.diagram.dataBind();
                }, 100);
            }
        }
        this.diagram.tool = DiagramTools.SingleSelect | DiagramTools.ZoomPan;
        this.vueInstance.connectorPending = false;
    }

    mouseDown(args) {
        this.vueInstance.connectorPending = true;
        this.diagram.drawingObject = {
            id: shortId.uuid(),
            type: "Orthogonal",
            sourceID: this.node.id,
            targetID: "",
            targetPortID: "",
            version: "NoHistory" // mark connector to be removed from undo/redo stack
        };
        return super.mouseDown(args);
    }

    mouseLeave(args) {
        this.diagram.currentDrawingObject = undefined;
        this.diagram.dataBind();
        setTimeout(() => {
            this.diagram.remove(args.source);
        }, 0);
    }
}

// inside palette
export function paletteGetNodeDefaults(symbol) {
    symbol.width = 50;
    symbol.height = 50;
    symbol.style = { fill: "none" };
    symbol.style.strokeColor = "transparent";
    symbol.data = { typeId: symbol.id };
    symbol.annotations = [{ content: symbol.text }];
}

export const annotationConfig = {
    verticalAlignment: "Top",
    horizontalAlignment: "Center",
    style: {
        color: "#196A84",
        fontFamily: "Roboto",
        fontSize: 10,
        // fill: "#F3F5F7"
        fill: "none"
    },
    displacement: {
        x: 0,
        y: 10
    },
    visibility: false
};

// inside diagram
export function getConnectorDefaults(diagramSettings) {
    return function (conn) {
        conn.type = "Orthogonal";
        conn.style = {
            strokeWidth: 2,
            strokeColor: "#6C778B"
        };
        const [{ content = "" } = {}] = conn.annotations;
        conn.annotations = [
            {
                ...annotationConfig,
                content
            }
        ];
        if (content) {
            conn.annotations[0].style.fill = "#F3F5F7";
        }
        conn.targetDecorator = {
            shape: "Custom",
            pathData: "M0 6.66897C0.00215222 6.24581 0 5.75396 0 5.32897C1.8812 3.91732 7.9951 -0.583682 7.22917 0.0679785L1.53035 5.7668H11.77C11.8999 5.7668 12 5.87208 12 6.00195C12 6.13182 11.8999 6.2371 11.77 6.2371H1.53219L7.22917 11.9341C7.9951 12.5848 1.8812 8.07942 0 6.66897Z",
            style: {
                strokeWidth: 1,
                fill: "#6C778B",
                strokeColor: "#6C778B"
            }
        };
        if (diagramSettings.showBridges) {
            conn.constraints = (ConnectorConstraints.Default | ConnectorConstraints.DragSegmentThumb | ConnectorConstraints.DragTargetEnd | ConnectorConstraints.Bridging) & ~ConnectorConstraints.Drag;
        } else {
            conn.constraints = (ConnectorConstraints.Default | ConnectorConstraints.DragSegmentThumb | ConnectorConstraints.DragTargetEnd) & ~ConnectorConstraints.Drag & ~ConnectorConstraints.Bridging;
        }
    };
}

const portSettings = {
    shape: "Circle",
    style: {
        strokeColor: "#6C778B",
        strokeWidth: 1.5,
        fill: "#fff"
    },
    width: 6,
    height: 6,
    visibility: PortVisibility.Connect | PortVisibility.Hover,
    constraints: PortConstraints.Default
};

export function createPorts() {
    return [
        {
            id: "portLeft1",
            offset: { x: 0, y: 0.25 },
            ...portSettings
        },
        {
            id: "portLeft2",
            offset: { x: 0, y: 0.5 },
            ...portSettings
        },
        {
            id: "portLeft3",
            offset: { x: 0, y: 0.75 },
            ...portSettings
        },

        {
            id: "portBot1",
            offset: { x: 0.25, y: 1 },
            ...portSettings
        },
        {
            id: "portBot2",
            offset: { x: 0.5, y: 1 },
            ...portSettings
        },
        {
            id: "portBot3",
            offset: { x: 0.75, y: 1 },
            ...portSettings
        },

        {
            id: "portRight1",
            offset: { x: 1, y: 0.25 },
            ...portSettings
        },
        {
            id: "portRight2",
            offset: { x: 1, y: 0.5 },
            ...portSettings
        },
        {
            id: "portRight3",
            offset: { x: 1, y: 0.75 },
            ...portSettings
        },

        {
            id: "portTop1",
            offset: { x: 0.25, y: 0 },
            ...portSettings
        },
        {
            id: "portTop2",
            offset: { x: 0.5, y: 0 },
            ...portSettings
        },
        {
            id: "portTop3",
            offset: { x: 0.75, y: 0 },
            ...portSettings
        }
    ];
}

export function getAutostartShape(hasRecurrence) {
    if (hasRecurrence) {
        return symbols.GENERAL_AUTOSTART_PLUGIN;
    }
    return symbols.GENERAL_START_PLUGIN;
}

// inside diagram
export function curryGetNodeDefaults(diagramSettings) {
    return function getNodeDefaults(node) {
        let source;
        const { additionalOptions: { hasRecurrence } = {} } = node.addInfo || {};
        if (hasRecurrence !== undefined) {
            source = getAutostartShape(hasRecurrence);
        } else {
            source = symbols[node.data.typeId];
        }
        node.width = 64;
        node.height = 64;
        node.shape = {
            type: "Image",
            source,
            scale: "Meet"
        };
        node.style = {
            fill: "none",
            strokeWidth: 1.5,
            strokeColor: "none"
        };
        node.annotations = [
            {
                content: node.annotations[0].content,
                id: node.annotations[0].id,
                offset: {
                    x: 0.5,
                    y: 1.25
                },
                style: {
                    color: "#273858",
                    fontFamily: "Roboto",
                    fontSize: 10,
                    fill: "#F3F5F7",
                    strokeColor: "#F3F5F7",
                    strokeWidth: 5,
                    textWrapping: "WrapWithOverflow"
                },
                visibility: diagramSettings.showLabels,
                constraints: diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly,
                verticalAlignment: "Top"
            }
        ];
        if (
            node.properties.data.typeId === "GENERAL_START_PLUGIN"
            || node.properties.data.typeId === "EXTERNAL_SOURCE_PLUGIN"
            || node.properties.data.typeId === "START_BLOCKCHAIN_EVENT_PLUGIN"
            || checkExtraConditions(node.properties.data.typeId)
        ) {
            node.constraints = NodeConstraints.Default & ~NodeConstraints.InConnect & ~NodeConstraints.Shadow;
        } else {
            node.ports = createPorts();
        }
    };
}

// inside diagram
export function dropNode(ev, readOnly, diagram) {
    if (readOnly) {
        ev.cancel = true;
        return;
    }
    const { element } = ev;
    let label = element.annotations[0].content;
    const foundTypes = diagram.nodes.filter(node => node.data.typeId === element.data.typeId); // get all nodes of this type
    if (foundTypes.length) {
        // find first available increment of element type
        let count = 1;
        // eslint-disable-next-line no-loop-func
        while (foundTypes.find(node => node.annotations.length && node.annotations[0].content === `${label} (${count})`)) {
            count++;
        }
        label = `${label} (${count})`; // change label to increment found
    }
    ev.cancel = true;
    const node = {
        id: shortId.uuid(),
        annotations: [
            {
                content: label
            }
        ],
        offsetX: element.offsetX,
        offsetY: element.offsetY,
        data: { typeId: element.data.typeId },
        style: { strokeColor: "transparent" }
    };
    diagram.add(node);
}

// inside palette
export function getSymbolInfo(symbol) {
    return {
        description: {
            text: symbol.text,
            overflow: "Wrap",
            fontFamily: "Roboto",
            bold: false,
            fontSize: 10,
            color: "#273858"
        }
    };
}

export function paletteSetup(palette) {
    const sections = palette.accordionElement.element.children;
    // eslint-disable-next-line no-restricted-syntax
    for (const el of sections) {
        // set expand/collapse functionality
        // eslint-disable-next-line no-inner-declarations
        function toggleExpand() {
            el.classList.toggle("collapsed");
        }
        el.firstChild.removeEventListener("click", toggleExpand);
        el.firstChild.addEventListener("click", toggleExpand);
        // ////////////////////////////////

        // remove that pesky title that creates browser tooltips
        el.querySelectorAll(".e-symbol-draggable").forEach(symbol => {
            symbol.setAttribute("title", "");
        });
        // /////////////////////////////////
    }
}

function convertNode(node, index, diagramSettings) {
    return {
        id: node.uuid,
        annotations: [
            {
                id: node.label.uuid,
                content: node.label.value,
                visibility: diagramSettings.showLabels,
                constraints: diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly
            }
        ],
        offsetX: node.x,
        offsetY: node.y,
        data: { typeId: node.id },
        style: { strokeColor: "transparent" },
        outEdges: [...(node.outgoing || [])],
        inEdges: [...(node.incoming || [])],
        addInfo: { additionalOptions: { ...node.label.additionalOptions } },
        zIndex: 300 + index
    };
}

export const ports = {
    LEFT: "portLeft2",
    BOTTOM: "portBot2",
    RIGHT: "portRight2",
    TOP: "portTop2",

    LEFT1: "portLeft1",
    BOTTOM1: "portBot1",
    RIGHT1: "portRight1",
    TOP1: "portTop1",

    LEFT3: "portLeft3",
    BOTTOM3: "portBot3",
    RIGHT3: "portRight3",
    TOP3: "portTop3",

    portLeft1: "LEFT1",
    portLeft2: "LEFT",
    portLeft3: "LEFT3",

    portTop1: "TOP1",
    portTop2: "TOP",
    portTop3: "TOP3",

    portRight1: "RIGHT1",
    portRight2: "RIGHT",
    portRight3: "RIGHT3",

    portBot1: "BOTTOM1",
    portBot2: "BOTTOM",
    portBot3: "BOTTOM3"
};
export function convertConnector(connector, diagramSettings) {
    return {
        id: connector.uuid,
        type: "Orthogonal",
        targetPortID: ports[connector.endSide],
        sourcePortID: ports[connector.fromSide],
        sourceID: connector.from,
        targetID: connector.to,
        style: {
            strokeColor: "#3E71D2"
        },
        annotations: [
            {
                id: connector.label.uuid,
                content: connector.label.value,
                visibility: diagramSettings.showLabels,
                constraints: diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly
            }
        ],
        segments: connector.segments || [{ type: "Orthogonal" }]
    };
}

export function diagramTemplate(nodesArr, connectorsArr, diagramSettings) {
    const diagram = {
        ...diagramConfig,
        // constraints: diagramSettings.showBridges ? 502 : 500, // (DiagramConstraints.Default | DiagramConstraints.Bridging) : DiagramConstraints.Default
        nodes: nodesArr,
        connectors: connectorsArr
    };
    if (diagramSettings.showGrid) {
        diagram.snapSettings.constraints = 15; // SnapConstraints.ShowLines | SnapConstraints.SnapToLines,
    } else {
        diagram.snapSettings.constraints = 12; // SnapConstraints.SnapToLines,
    }
    return JSON.stringify(diagram);
}

export function convertOldDiagram({ content = [], connectors = [] } = {}, diagramSettings) {
    annotationConfig.visibility = diagramSettings.showLabels;
    annotationConfig.constraints = diagramSettings.showLabels ? AnnotationConstraints.None : AnnotationConstraints.ReadOnly;
    if (!content.length) {
        content = [
            {
                uuid: "ToBeDeleted1",
                id: "ToBeDeleted1",
                x: -256,
                y: 224,
                width: 100,
                height: 100,
                label: {
                    value: "",
                    uuid: "gdJkG",
                    x: 0,
                    y: 0,
                    labelOf: "ToBeDeleted1",
                    pluginUUID: "ToBeDeleted1",
                    pluginID: "ToBeDeleted1"
                },
                anchoredPlugins: [],
                outgoing: [],
                incoming: []
            }
        ];
    }
    let nodesArr;
    // eslint-disable-next-line no-lone-blocks
    {
        // ONE NODE BUG
        // this block of code is only useful because of a library bug that crashes the diagram if you load only a single node
        // when the bug is fixed this and the code that deletes the extra node we add here, from the main diagram file should be deleted
        // look for the comment: "ONE NODE BUG" in the other file when you delete this
        if (content.length === 1) {
            nodesArr = [
                ...content,
                {
                    ...JSON.parse(JSON.stringify(content[0])),
                    uuid: "ToBeDeleted",
                    version: "NoHistory" // mark node to be removed from undo/redo stack
                }
            ];
        } else {
            nodesArr = content;
        }
    }
    const nodes = nodesArr.map((el, index) => convertNode(el, index, diagramSettings));
    const connectorsArr = connectors.map(el => convertConnector(el, diagramSettings));
    return diagramTemplate(nodes, connectorsArr, diagramSettings);
}

function revertNode(node) {
    const [{ content = "", id = "" } = {}] = node.annotations || [];
    const x = node.offsetX;
    const y = node.offsetY;
    const { additionalOptions } = node.addInfo || {};
    const result = {
        uuid: node.id,
        id: node.properties.data.typeId,
        x,
        y,
        width: 100,
        height: 100,
        label: {
            value: content,
            uuid: id,
            x: x + 55,
            y,
            labelOf: node.id,
            pluginUUID: node.id,
            pluginID: node.properties.data.typeId
        },
        anchoredPlugins: [],
        outgoing: [...(node.outEdges || [])],
        incoming: [...(node.inEdges || [])]
    };
    if (additionalOptions) {
        result.label.additionalOptions = additionalOptions;
    }
    return result;
}

function revertConector(conn) {
    const path = conn.segments.map(({ points }) => points[0]);
    const lastPoint = conn.segments[conn.segments.length - 1].points[1];
    path.push(lastPoint);
    const segments = conn.segments.map(({ properties, points }) => ({ points: { ...points }, ...properties }));
    const [{ id: annotId, content = "" } = {}] = conn.annotations || [];
    return {
        uuid: conn.id,
        from: conn.sourceID,
        to: conn.targetID,
        endSide: ports[conn.targetPortID],
        fromSide: ports[conn.sourcePortID],
        connectorCode: conn.id,
        path,
        label: {
            value: content,
            uuid: annotId,
            labelOf: conn.id,
            ...path[0]
        },
        segments
    };
}

export function revertToOldDiagram(data) {
    const content = data.content.map(el => revertNode(el));
    const connectors = data.connectors.map(el => revertConector(el));
    return {
        worldData: {
            width: 2000,
            height: 2200
        },
        gridView: {
            padding: 500,
            cellWidth: 100,
            cellHeight: 100,
            rows: 12,
            cols: 10
        },
        content,
        connectors
    };
}
